import React, { useEffect, useState } from "react";
import { api } from "@/api/client";
import { Section, Button, Input, Select, Textarea, Empty, Badge } from "@/components/ui/kit";
import { toast } from "sonner";
import { fmtDate } from "@/utils/format";
import { Plus, Image as ImageIcon, FileText, Video } from "lucide-react";

export default function ImpactUpdates() {
  const [items, setItems] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [bens, setBens] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const today = new Date().toISOString().slice(0,10);
  const [f, setF] = useState({ donationPurposeId:"", beneficiaryId:"", title:"", description:"", updateDate: today, mediaUrl:"", mediaType:"IMAGE", mediaCaption:"" });

  const load = () => api.get("/impact-updates").then(r => setItems(r.data.data));
  useEffect(() => {
    load();
    api.get("/donation-purposes").then(r => setPurposes(r.data.data));
    api.get("/beneficiaries", { params: { size: 200 } }).then(r => setBens(r.data.data.content));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const media = f.mediaUrl ? [{ type: f.mediaType, url: f.mediaUrl, caption: f.mediaCaption }] : [];
      await api.post("/impact-updates", {
        donationPurposeId: f.donationPurposeId ? Number(f.donationPurposeId) : null,
        beneficiaryId: f.beneficiaryId ? Number(f.beneficiaryId) : null,
        title: f.title, description: f.description, updateDate: f.updateDate, media,
      });
      toast.success("Impact update published");
      setOpenAdd(false); setF({ ...f, title:"", description:"", mediaUrl:"", mediaCaption:"" });
      load();
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
  };

  return (
    <div className="space-y-6">
      <Section title="Impact Updates" kicker="Stories of impact"
        right={<Button onClick={()=>setOpenAdd(v=>!v)} data-testid="add-impact-btn"><Plus className="w-4 h-4"/> New update</Button>} />

      {openAdd && (
        <form onSubmit={save} className="rounded-xl border border-emerald-900/10 bg-white p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="impact-add-form">
          <Select label="Purpose (project/activity)" testid="new-imp-purpose" value={f.donationPurposeId} onChange={e=>setF(v=>({...v, donationPurposeId: e.target.value}))}>
            <option value="">— none —</option>
            {purposes.map(p => <option key={p.id} value={p.id}>{p.type} • {p.name}</option>)}
          </Select>
          <Select label="Beneficiary" testid="new-imp-ben" value={f.beneficiaryId} onChange={e=>setF(v=>({...v, beneficiaryId: e.target.value}))}>
            <option value="">— none —</option>
            {bens.map(b => <option key={b.beneficiaryId} value={b.beneficiaryId}>{b.name}</option>)}
          </Select>
          <Input label="Title" required testid="new-imp-title" value={f.title} onChange={e=>setF(v=>({...v, title: e.target.value}))} />
          <Input label="Date" type="date" testid="new-imp-date" value={f.updateDate} onChange={e=>setF(v=>({...v, updateDate: e.target.value}))} />
          <div className="sm:col-span-2 lg:col-span-4">
            <Textarea label="Description" testid="new-imp-desc" rows={3} value={f.description} onChange={e=>setF(v=>({...v, description: e.target.value}))} />
          </div>
          <Select label="Media type" testid="new-imp-media-type" value={f.mediaType} onChange={e=>setF(v=>({...v, mediaType: e.target.value}))}>
            <option value="IMAGE">Image</option><option value="VIDEO">Video</option><option value="REPORT">Report</option>
          </Select>
          <Input label="Media URL" testid="new-imp-media-url" value={f.mediaUrl} onChange={e=>setF(v=>({...v, mediaUrl: e.target.value}))} placeholder="https://..." />
          <Input label="Caption" testid="new-imp-media-caption" value={f.mediaCaption} onChange={e=>setF(v=>({...v, mediaCaption: e.target.value}))} />
          <div className="sm:col-span-2 lg:col-span-4">
            <Button type="submit" data-testid="save-impact-btn">Publish update</Button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {items.map(u => (
          <article key={u.id} data-testid={`impact-card-${u.id}`} className="rounded-xl border border-emerald-900/10 bg-white overflow-hidden hover:shadow-sm transition-shadow">
            {u.media?.find(m => m.type === "IMAGE") && (
              <img src={u.media.find(m => m.type === "IMAGE").url} alt="" className="w-full h-48 object-cover" />
            )}
            <div className="p-5">
              <div className="flex items-center gap-2">
                <div className="overline">{fmtDate(u.date)}</div>
                {u.purposeName && <Badge tone="brand">{u.purposeName}</Badge>}
                {u.beneficiaryName && <Badge tone="warm">{u.beneficiaryName}</Badge>}
              </div>
              <h3 className="mt-2 font-display text-lg font-bold text-emerald-950">{u.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground line-clamp-3">{u.description}</p>
              <div className="mt-3 flex gap-3 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {u.media?.filter(m=>m.type==="IMAGE").length || 0}</span>
                <span className="inline-flex items-center gap-1"><Video className="w-3 h-3" /> {u.media?.filter(m=>m.type==="VIDEO").length || 0}</span>
                <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" /> {u.media?.filter(m=>m.type==="REPORT").length || 0}</span>
              </div>
            </div>
          </article>
        ))}
        {!items.length && <div className="col-span-full"><Empty title="No impact updates yet" note="Publish stories to build donor trust." testid="impact-empty" /></div>}
      </div>
    </div>
  );
}
